import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { govconClient } from '../../../lib/connect/clients';
import { BlockOverlay } from '../../ProposalDashboard/DocumentViewer/BlockOverlay';
/** Uses the shared page renderer and overlay with coordinates from the original PDF. */ export function OriginalPdfSource({ documentId, quote }) {
    const cacheScope = useId();
    const [requestedPage, setRequestedPage] = useState(0);
    const page = useQuery({
        queryKey: [
            'citation-pdf-page',
            documentId,
            cacheScope,
            requestedPage,
            quote
        ],
        queryFn: ()=>govconClient.getPricingSheetPage({
                sourceDocumentId: documentId,
                pageNumber: requestedPage,
                quote
            }),
        retry: false,
        staleTime: 0,
        gcTime: 0
    });
    if (page.isPending) return /*#__PURE__*/ _jsx("output", {
        className: "block text-sm text-muted-foreground",
        children: "Finding the cited passage in the original PDF…"
    });
    if (page.isError) return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "The original PDF page could not be opened. You can still use Readable text."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                onClick: ()=>void page.refetch(),
                children: "Retry PDF page"
            })
        ]
    });
    const data = page.data;
    const blocks = data.blocks.filter((block)=>block.blockId.startsWith('citation-quote/')).map((block)=>({
            id: block.blockId,
            type: block.type,
            content: block.content,
            boundingBox: {
                x: block.x,
                y: block.y,
                width: block.width,
                height: block.height
            },
            confidence: 1
        }));
    const highlighted = new Set(blocks.map((block)=>block.id));
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex min-h-0 flex-1 flex-col gap-3",
        children: [
            data.totalPages > 1 ? /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        disabled: data.pageNumber <= 1,
                        onClick: ()=>setRequestedPage(data.pageNumber - 1),
                        children: "Previous page"
                    }),
                    /*#__PURE__*/ _jsxs("output", {
                        children: [
                            "Page ",
                            data.pageNumber,
                            " of ",
                            data.totalPages || 'unknown'
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        disabled: !data.totalPages || data.pageNumber >= data.totalPages,
                        onClick: ()=>setRequestedPage(data.pageNumber + 1),
                        children: "Next page"
                    })
                ]
            }) : null,
            quote ? /*#__PURE__*/ _jsx("p", {
                className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: data.quoteMatched ? 'Matching quote highlighted on the original page.' : 'The quoted text could not be located on this page. No position has been guessed.'
            }) : null,
            /*#__PURE__*/ _jsx("div", {
                className: "min-h-0 flex-1 overflow-y-auto",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "relative",
                    children: [
                        /*#__PURE__*/ _jsx("img", {
                            className: "w-full",
                            src: data.imageDataUrl,
                            alt: `Original source, page ${data.pageNumber}`
                        }),
                        /*#__PURE__*/ _jsx(BlockOverlay, {
                            blocks: blocks,
                            pageDimensions: {
                                width: data.pageWidth,
                                height: data.pageHeight
                            },
                            selectedBlockId: null,
                            highlightedBlockIds: highlighted,
                            onBlockSelect: ()=>{}
                        })
                    ]
                })
            })
        ]
    });
}
